import { useState, useCallback, useEffect } from 'react'

export interface ApproximateLocation {
  latitude: number
  longitude: number
  city?: string
  region?: string
  country?: string
  accuracy?: 'country' | 'region' | 'city'
}

export interface UseApproximateLocationReturn {
  location: ApproximateLocation | null
  isLoading: boolean
  error: string | null
  requestLocation: () => void
}

/**
 * Hook to get approximate user location via IP geolocation
 * Falls back to a reasonable default if IP geolocation fails
 */
export function useApproximateLocation(): UseApproximateLocationReturn {
  const [location, setLocation] = useState<ApproximateLocation | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestLocation = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Try IP-based geolocation using a free service
      // Using ipapi.co which provides free tier with reasonable accuracy
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to get approximate location')
      }

      const data = await response.json()
      
      if (data.latitude && data.longitude) {
        setLocation({
          latitude: parseFloat(data.latitude),
          longitude: parseFloat(data.longitude),
          city: data.city,
          region: data.region,
          country: data.country_name,
          accuracy: 'city'
        })
      } else {
        throw new Error('Invalid location data received')
      }
    } catch (err) {
      // Fallback to US center (approximate center of continental US)
      setLocation({
        latitude: 39.8283, // Approximate center of US
        longitude: -98.5795,
        country: 'United States',
        accuracy: 'country'
      })
      setError(err instanceof Error ? err.message : 'Failed to get approximate location')
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    location,
    isLoading,
    error,
    requestLocation
  }
}

